<!-- eslint-disable vue/valid-v-model -->
<template>
  <div class="custom-editor" ref="wrapper">
    <quill-editor
      ref="qeditor"
      v-model:content="content"
      content-type="html"
      @input="updateField"
      :options="editorOption"
    >
      >
    </quill-editor>
    <emoji-picker
      v-if="advanced && showEmojiPicker"
      ref="emojiPicker"
      class="custom-editor__emoji-picker"
      @select="insertEmoji"
      @close="closeEmojiPicker"
    ></emoji-picker>
  </div>
</template>

<script>
import 'quill/dist/quill.core.css';
import 'quill/dist/quill.snow.css';
import 'quill/dist/quill.bubble.css';
import { quillEditor, Quill } from 'vue-quill-editor';
import 'quill-divider';
import ImageResize from 'quill-image-resize-vue';
Quill.register('modules/imageResize', ImageResize);
import MagicUrl from 'quill-magic-url';
import EmojiPicker from './EmojiPicker.vue';
Quill.register('modules/magicUrl', MagicUrl);
var icons = Quill.import('ui/icons');
icons['undo'] = `<svg viewbox="0 0 18 18">
    <polygon class="ql-fill ql-stroke" points="6 10 4 12 2 10 6 10"></polygon>
    <path class="ql-stroke" d="M8.09,13.91A4.6,4.6,0,0,0,9,14,5,5,0,1,0,4,9"></path>
  </svg>`;
icons['redo'] = `<svg viewbox="0 0 18 18">
    <polygon class="ql-fill ql-stroke" points="12 10 14 12 16 10 12 10"></polygon>
    <path class="ql-stroke" d="M9.91,13.91A4.6,4.6,0,0,1,9,14a5,5,0,1,1,5-5"></path>
  </svg>`;
icons['emoji'] = `<svg viewbox="0 0 18 18">
    <circle class="ql-stroke" cx="9" cy="9" r="7"></circle>
    <circle class="ql-fill" cx="6.5" cy="7.5" r="1"></circle>
    <circle class="ql-fill" cx="11.5" cy="7.5" r="1"></circle>
    <path class="ql-stroke" d="M5.5,10.5a4,4,0,0,0,7,0"></path>
  </svg>`;

export default {
  components: {
    'quill-editor': quillEditor,
    EmojiPicker,
  },
  props: {
    value: {},
    // Editor del asesor: añade color de texto y emoticonos. Sin él, el editor es el simplificado del cliente
    advanced: { type: Boolean, default: false },
  },
  computed: {
    // a computed getter
    editorOption: function() {
      var Delta = Quill.import('delta');
      const vm = this;
      const advanced = this.advanced;

      // `this` points to the vm instance
      return {
        modules: {
          history: {
            delay: 1000,
            maxStack: 500,
            userOnly: true,
          },

          clipboard: {
            matchVisual: false,
            matchers: [
              [
                Node.ELEMENT_NODE,
                function(node, delta) {
                  const attributes = { background: false };
                  // El cliente no usa color: se descarta el que traiga lo pegado (p. ej. de Word)
                  if (!advanced) {
                    attributes.color = false;
                  }
                  return delta.compose(new Delta().retain(delta.length(), attributes));
                },
              ],

              // [Node.TEXT_NODE, (node, delta) => {
              //     if(node.data.match(/^\s/) ) {
              //         const Delta = Quill.import('delta');
              //         return new Delta().insert(node.data);
              //     }
              //     return delta;
              // }],
            ],
          },
          imageResize: {
            // See optional "config" below
          },
          magicUrl: true,
          divider: true,
          toolbar: {
            container: [
              ['bold', 'italic', 'strike', 'underline', 'link'],
              // La primera opción del selector de color restablece el color por defecto
              ...(advanced ? [[{ color: [] }]] : []),
              [{ header: [1, 2, 3, 4, 5, 6, false] }],
              [{ list: 'bullet' }, { list: 'ordered' }],
              ['blockquote'],
              ['image'],
              ['divider'],
              ...(advanced ? [['emoji']] : []),
              ['undo', 'redo'],
            ],
            handlers: {
              emoji() {
                vm.toggleEmojiPicker();
              },
              redo() {
                this.quill.history.redo();
              },
              undo() {
                this.quill.history.undo();
              },
              //redo: this.myRedo,
            },
          },
        },
      };
    },
  },
  data() {
    return {
      content: this.value,
      showEmojiPicker: false,
      // Posición del cursor al abrir el selector: el clic en un emoticono quita el foco
      emojiRange: null,
    };
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.onDocumentMousedown);
    document.removeEventListener('keydown', this.onDocumentKeydown);
  },
  methods: {
    getQuill() {
      return this.$refs.qeditor ? this.$refs.qeditor.quill : null;
    },
    toggleEmojiPicker() {
      if (this.showEmojiPicker) {
        this.closeEmojiPicker();
        return;
      }
      const quill = this.getQuill();
      if (quill) {
        this.emojiRange = quill.getSelection() || { index: quill.getLength() - 1, length: 0 };
      }
      this.showEmojiPicker = true;
      document.addEventListener('mousedown', this.onDocumentMousedown);
      // El foco se queda en el editor, así que Escape se escucha en el documento
      document.addEventListener('keydown', this.onDocumentKeydown);
    },
    closeEmojiPicker() {
      this.showEmojiPicker = false;
      document.removeEventListener('mousedown', this.onDocumentMousedown);
      document.removeEventListener('keydown', this.onDocumentKeydown);
    },
    onDocumentKeydown(event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        this.closeEmojiPicker();
      }
    },
    onDocumentMousedown(event) {
      const picker = this.$refs.emojiPicker ? this.$refs.emojiPicker.$el : null;
      const toolbarButton = this.$refs.wrapper
        ? this.$refs.wrapper.querySelector('.ql-emoji')
        : null;
      if (
        (picker && picker.contains(event.target)) ||
        (toolbarButton && toolbarButton.contains(event.target))
      ) {
        return;
      }
      this.closeEmojiPicker();
    },
    insertEmoji(emoji) {
      const quill = this.getQuill();
      if (!quill) {
        return;
      }
      const range = this.emojiRange || quill.getSelection(true);
      if (range.length) {
        quill.deleteText(range.index, range.length, 'user');
      }
      quill.insertText(range.index, emoji, 'user');
      const index = range.index + emoji.length;
      quill.setSelection(index, 0, 'user');
      this.emojiRange = { index, length: 0 };
    },
    updateField: function(value) {
      this.$emit('input', String(value));
    },
    myUndo: function() {
      //let myEditor = this.$refs.qeditor.getEditor();
      return this.$refs.qeditor.history.undo();
    },
    myRedo: function() {
      //let myEditor = this.$refs.qeditor.getEditor();
      return this.$refs.qeditor.history.redo();
    },
  },
  watch: {
    value: {
      handler(newValue, oldValue) {
        // Note: `newValue` will be equal to `oldValue` here
        // on nested mutations as long as the object itself
        // hasn't been replaced.
        this.content = newValue;
      },
      deep: true,
    },
  },
};
</script>

<style>
.custom-editor {
  position: relative;
}
.custom-editor__emoji-picker {
  position: absolute !important;
  top: 44px;
  right: 8px;
  z-index: 20;
}
.ql-container {
  height: 300px !important;
}
.ql-editor p {
  margin-bottom: 15px !important;
}
.ql-clipboard {
  white-space: normal;
}

.ql-undo {
  content: 'undo';
  font-family: 'Material Icons';
}
</style>
